import React from 'react';

/**
 * Cross-links between inventory document list/detail screens.
 * Icons match main menu (dashboardMenuUtils / expandPurchaseRequisitionWebMenus).
 */
export const INVENTORY_DOC_SHORTCUTS = [
    {
        id: 'requisition',
        label: 'Requisition',
        icon: 'pi pi-list',
        // Explicit status query so hash always changes (bare #/purchaseRequisition keeps
        // sessionStorage list filter, often still PURCHASE_ORDER → click looks broken).
        href: '#/purchaseRequisition?PurchaseRequisition',
        mobileHref: '#/inventoryManagementMobile?PurchaseRequisition'
    },
    {
        id: 'purchaseOrder',
        label: 'Purchase Order',
        icon: 'pi pi-shopping-cart',
        href: '#/purchaseRequisition?PurchaseOrder',
        mobileHref: '#/purchaseRequisition?PurchaseOrder'
    },
    {
        id: 'deliveryNote',
        label: 'Delivery Note',
        icon: 'pi pi-inbox',
        href: '#/purchaseRequisition?DeliveryNote',
        mobileHref: '#/inventoryManagementMobile?DeliveryNote'
    },
    {
        id: 'return',
        label: 'Return Note',
        icon: 'pi pi-replay',
        href: '#/purchaseRequisition?ReturnNote',
        mobileHref: '#/inventoryManagementMobile?ReturnNote'
    },
    {
        id: 'invoice',
        label: 'Invoice',
        icon: 'pi pi-file-pdf',
        href: '#/purchaseOrder',
        mobileHref: '#/purchaseOrderMobile'
    }
];

function stripHash(hash) {
    return String(hash || window.location.hash || '')
        .replace(/^#\/?/, '')
        .split('?')[0]
        .toLowerCase();
}

function hashQuery(hash) {
    const raw = String(hash || window.location.hash || '');
    const qIndex = raw.indexOf('?');
    if (qIndex < 0) {
        return '';
    }
    // Keep only status-relevant first segment when till appended "?token_url=..."
    return raw.substring(qIndex + 1).replace(/\?/g, '&');
}

function detectActiveIdFromHash(hash) {
    const path = stripHash(hash);
    const query = hashQuery(hash);
    const queryToken = query.split('&')[0];

    if (path === 'purchaseorder' || path.startsWith('purchaseordersave')
        || path === 'purchaseordermobile' || path.startsWith('purchaseordermobilesave')) {
        return 'invoice';
    }

    if (path === 'purchaserequisitionsave' || path === 'inventorymanagementsavemobile'
        || path === 'purchaserequisitionsavemobile') {
        if (query.indexOf('mode=delivery') >= 0) {
            return 'deliveryNote';
        }
        if (query.indexOf('mode=returnNote') >= 0 || query.indexOf('mode=return') >= 0) {
            return 'return';
        }
        // Loaded detail status not in hash — rely on activeId prop from the page.
        return null;
    }

    if (path === 'purchaserequisition' || path === 'inventorymanagementmobile'
        || path === 'purchaserequisitionmobile') {
        if (!queryToken || /^(purchaserequisition|open|requisition)$/i.test(queryToken)) {
            return 'requisition';
        }
        if (/^purchaseorder$/i.test(queryToken)) {
            return 'purchaseOrder';
        }
        if (/^deliverynote$/i.test(queryToken)) {
            return 'deliveryNote';
        }
        if (/^(return|returnnote)$/i.test(queryToken)) {
            return 'return';
        }
    }

    return null;
}

/**
 * @param {object} props
 * @param {string} [props.activeId] Force active shortcut id (overrides hash detection)
 * @param {boolean} [props.mobile] Use mobile hrefs
 * @param {string} [props.className]
 */
export default function PurchaseRequisitionNavShortcuts({activeId, mobile = false, className = ''}) {
    const hash = typeof window !== 'undefined' ? (window.location.hash || '') : '';
    const resolvedActiveId = activeId || detectActiveIdFromHash(hash);

    return (
        <nav
            className={`pr-doc-shortcuts${mobile ? ' pr-doc-shortcuts--mobile' : ''}${className ? ` ${className}` : ''}`}
            aria-label="Document page shortcuts"
        >
            {INVENTORY_DOC_SHORTCUTS.map(item => {
                const href = mobile ? item.mobileHref : item.href;
                const active = resolvedActiveId === item.id;
                return (
                    <a
                        key={item.id}
                        href={href}
                        className={`pr-doc-shortcuts__link${active ? ' is-active' : ''}`}
                        aria-current={active ? 'page' : undefined}
                    >
                        {item.icon &&
                            <i className={`${item.icon} pr-doc-shortcuts__icon`} aria-hidden="true"/>
                        }
                        <span className="pr-doc-shortcuts__label">{item.label}</span>
                    </a>
                );
            })}
        </nav>
    );
}

/**
 * Resolve active shortcut id from PR family status (list or detail).
 */
export function shortcutIdForRequisitionStatus(requisitionStatus) {
    switch (requisitionStatus) {
        case 'PURCHASE_ORDER':
            return 'purchaseOrder';
        case 'DELIVERY_NOTE':
            return 'deliveryNote';
        case 'RETURN':
            return 'return';
        case 'OPEN':
        default:
            return 'requisition';
    }
}
